import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, message, Spin } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import { problemTypesHeirarchical } from '../../thunks/problem_types_thunks';
import {
	downloadFirstRankSuppliers,
	getUploadRankBulkStatus,
	updateFirstRankSupplierBulk,
} from '../../thunks/supplier_private_networks_thunks';
import { getAllLeafNodes } from '../../utils/DataAccessUtils';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import {
	fetchSupplierFacilitiesInPrivateNetworkForBuyer,
	fetchSupplierFacilitiesInPrivateNetworkForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import OWPoll from '../common/OWPoll';

const FirstRankSupplierUploadButton: FC<any> = ({
	getRankFilters,
	fetchAllLocations,
	fetchAllProblemTypes,
	fetchAllSupplierPrivateNetwork,
	updateFirstRankSuppliers,
	downloadFirstRankSuppliers,
	bulkRankUploadStatus,
}): React.ReactElement => {
	const [uploadStarting, setUploadStarting] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [uploadModalVisible, setUploadModalVisible] = useState(false);

	const [allLocations, setAllLocations] = useState([]);
	const [allProblemTypes, setAllProblemTypes] = useState([]);
	const [allSuppliers, setAllSuppliers] = useState([]);
	const [uploadingRank, setUploadingRank] = useState(false);
	const [uploadInitiated, setUploadInitiated] = useState(false);

	const canUpload = useCallback(() => {
		return new Promise((resolve, reject) => {
			bulkRankUploadStatus()
				.then((res) => {
					if (res && res.length > 0) {
						message.error('Rank update already in progress!');
						resolve(false);
					} else {
						resolve(true);
					}
				})
				.catch(() => resolve(true));
		});
	}, [bulkRankUploadStatus]);

	const onUploadSuppliersClick = useCallback(() => {
		setUploadStarting(true);
		canUpload()
			.then((res) => {
				if (!res) {
					setUploadStarting(false);
					return;
				}

				Promise.all([fetchAllLocations(), fetchAllProblemTypes(), fetchAllSupplierPrivateNetwork()])
					.then((values) => {
						setAllLocations(values[0]);
						const problemTypes = getAllLeafNodes(values[1]);
						setAllProblemTypes(problemTypes);
						setAllSuppliers(values[2]);
						setUploadModalVisible(true);
					})
					.catch(message.error)
					.finally(() => setUploadStarting(false));
			})
			.catch(() => setUploadStarting(false));
	}, [fetchAllProblemTypes, fetchAllSupplierPrivateNetwork, fetchAllLocations, canUpload]);

	const _hideUploadModal = () => setUploadModalVisible(false);

	const onDataLoad = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				setUploadingRank(true);
				setUploadInitiated(true);
				updateFirstRankSuppliers(results.validData)
					.then((res) => {
						resolve(res);
					})
					.catch((err) => {
						reject(err);
						setUploadInitiated(false);
					})
					.finally(() => setUploadingRank(false));
			});
		},
		[updateFirstRankSuppliers]
	);

	const fields = useMemo(
		() => [
			{
				label: 'Location',
				key: 'locationId',
				type: 'enum',
				alternates: ['Location Store Id'],
				config: {
					options: allLocations.map((_) => ({
						label: `${_.name}${_.storeId ? ` - ${_.storeId}` : ''}`,
						value: _.id,
						alternativeNames: [_.name, ...(_.storeId ? [_.storeId] : [])],
					})),
				},
				constraints: [{ type: 'required' }],
			},
			{
				label: 'Problem Type',
				key: 'problemTypeId',
				type: 'enum',
				config: {
					options: allProblemTypes.map((_) => ({
						label: _.hierarchyName,
						value: _.id,
					})),
				},
				constraints: [{ type: 'required' }],
			},
			{
				label: 'Suppliers',
				key: 'supplierFacilityId',
				type: 'enum',
				config: {
					options: allSuppliers.map((_) => ({
						label: _.name,
						value: _.id,
					})),
				},
				constraints: [{ type: 'required' }],
			},
		],
		[allLocations, allProblemTypes, allSuppliers]
	);

	const onDownloadRank1Suppliers = useCallback(() => {
		setDownloading(true);
		downloadFirstRankSuppliers(getRankFilters())
			.catch(message.error)
			.finally(() => setDownloading(false));
	}, [downloadFirstRankSuppliers, getRankFilters]);

	const uploadMenu = useMemo(
		() => (
			<Menu>
				<Menu.Item onClick={onUploadSuppliersClick}>Rank 1 Suppliers</Menu.Item>
			</Menu>
		),
		[onUploadSuppliersClick]
	);

	return (
		<>
			{uploadingRank ? null : (
				<OWPoll
					key="rank-supplier"
					id="rank-supplier"
					fetchStatusPromises={[bulkRankUploadStatus]}
					successMessage="Uploading Supplier Ranking Completed!"
					message="Supplier Ranking Upload in progress!"
					successMessageMandatory={uploadInitiated}
					renderElementWithProps={(props) => (
						<Dropdown overlay={uploadMenu} {...props}>
							<Button
								key={1}
								style={{ marginRight: 16 }}
								size="large"
								className="inline-block-visible-md"
							>
								<div className="flex flex-row items-center">
									<span>Upload</span>
									<div className="ml-2">
										{uploadStarting ? <Spin size="small" /> : <DownOutlined />}
									</div>
								</div>
							</Button>
						</Dropdown>
					)}
				/>
			)}
			{uploadModalVisible ? (
				<UploadWithSampleModal
					type="Rank 1 Suppliers"
					title="Upload Rank 1 Suppliers"
					downloadText="Do you want to download exisiting rank 1 suppliers?"
					sampleFileText="Download Rank 1 Suppliers"
					downloading={downloading}
					onCancel={_hideUploadModal}
					onDownloadSample={onDownloadRank1Suppliers}
					fields={fields}
					maxRecords={100}
					onDataLoad={onDataLoad}
					mappingConfidenceThreshold={0.1}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAllLocations: () =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readLite({
						no_pagination: true,
						noIsActiveFilter: true,
				  })
				: locationsRestCrudThunksForBuyer.readLite({ no_pagination: true, noIsActiveFilter: true })
		),
	fetchAllProblemTypes: () => dispatch(problemTypesHeirarchical(ownProps.userType)({})),
	fetchAllSupplierPrivateNetwork: () =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchSupplierFacilitiesInPrivateNetworkForSupplier({ no_pagination: true })
				: fetchSupplierFacilitiesInPrivateNetworkForBuyer({ no_pagination: true })
		),
	updateFirstRankSuppliers: (data) =>
		dispatch(updateFirstRankSupplierBulk(ownProps.userType)(data)),
	downloadFirstRankSuppliers: (params) =>
		dispatch(downloadFirstRankSuppliers(ownProps.userType)(params)),
	bulkRankUploadStatus: () => dispatch(getUploadRankBulkStatus(ownProps.userType)()),
});

const ComponentWithoutUserType = connect(
	mapStateToProps,
	mapDispatchToProps
)(FirstRankSupplierUploadButton);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
